import { useEffect, useRef, useState } from "react";
import { Copy, Forward, Reply, Smile } from "lucide-react";
import { ChatQuickReactionStrip } from "./ChatQuickReactionStrip";

type Props = {
  mine: boolean;
  myReaction?: string | null;
  onReply: () => void;
  onCopy: () => void;
  onForward: () => void;
  onReact: (emoji: string) => void;
};

/**
 * Desktop only (hover + fine pointer) — smile + flèche répondre à côté de la bulle (style Messenger).
 */
export function MessageHoverActions({ mine, myReaction, onReply, onCopy, onForward, onReact }: Props) {
  const [reactOpen, setReactOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!reactOpen) return undefined;
    const onDoc = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (t && rootRef.current?.contains(t)) return;
      setReactOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setReactOpen(false);
    };
    document.addEventListener("pointerdown", onDoc, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [reactOpen]);

  return (
    <div
      ref={rootRef}
      className={`chat-msg-hover-actions ${mine ? "chat-msg-hover-actions--mine" : "chat-msg-hover-actions--other"} ${
        reactOpen ? "is-open" : ""
      }`}
    >
      {reactOpen ? (
        <div
          className={`chat-msg-hover-react ${mine ? "chat-msg-hover-react--mine" : ""}`}
          role="menu"
          aria-label="Réactions rapides"
        >
          <ChatQuickReactionStrip
            compact
            myReaction={myReaction}
            onReact={(emoji) => {
              setReactOpen(false);
              onReact(emoji);
            }}
          />
        </div>
      ) : null}
      <div className="chat-msg-hover-actions__bar">
        <button
          type="button"
          className="chat-msg-hover-actions__btn"
          title="Réagir"
          aria-label="Réagir"
          aria-expanded={reactOpen}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setReactOpen((o) => !o);
          }}
        >
          <Smile className="size-3.5" strokeWidth={2} />
        </button>
        <button
          type="button"
          className="chat-msg-hover-actions__btn"
          title="Répondre"
          aria-label="Répondre"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setReactOpen(false);
            onReply();
          }}
        >
          <Reply className="size-3.5" strokeWidth={2} />
        </button>
        <button
          type="button"
          className="chat-msg-hover-actions__btn"
          title="Copier"
          aria-label="Copier"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setReactOpen(false);
            onCopy();
          }}
        >
          <Copy className="size-3.5" strokeWidth={2} />
        </button>
        <button
          type="button"
          className="chat-msg-hover-actions__btn"
          title="Transférer"
          aria-label="Transférer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setReactOpen(false);
            onForward();
          }}
        >
          <Forward className="size-3.5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
